import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { platformApiFetch } from './platformApi';
import { PLATFORM_ANALYTICS_DASHBOARD_TYPE } from '../dashboard/widgetCatalog';

function defaultRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 86400000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function mergeSeries(usageByDay, loginsByDay) {
  const map = new Map();
  for (const r of usageByDay || []) {
    map.set(r.date, {
      date: r.date,
      apiRequests: r.apiRequests,
      loginRollup: r.authLoginsRollup,
      auditRollup: r.auditEventsRollup,
      loginsExact: 0
    });
  }
  for (const r of loginsByDay || []) {
    const row = map.get(r.date) || {
      date: r.date,
      apiRequests: 0,
      loginRollup: 0,
      auditRollup: 0,
      loginsExact: 0
    };
    row.loginsExact = r.logins;
    map.set(r.date, row);
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}

function kpiSpanProps(config) {
  const s = (config && config.span) || {};
  return {
    xs: s.xs != null ? s.xs : 6,
    sm: s.sm != null ? s.sm : 4,
    md: s.md != null ? s.md : 2
  };
}

function kpiValueForCode(code, k) {
  switch (code) {
    case 'kpi_tenant_count':
      return k?.tenantCount;
    case 'kpi_active_tenants':
      return k?.activeTenants;
    case 'kpi_tenant_users':
      return k?.totalTenantUsers;
    case 'kpi_api_requests':
      return k?.tenantApiRequestsInRange;
    case 'kpi_logins_range':
      return k?.loginsInRange;
    case 'kpi_distinct_users_login':
      return k?.distinctTenantUsersWithLoginInRange;
    case 'kpi_proctoring_violations':
      return k?.proctoringViolationsTotal;
    case 'kpi_proctoring_snapshots':
      return k?.proctoringSnapshotsTotal;
    default:
      return undefined;
  }
}

function renderConfiguredKpi(w, k) {
  const sp = kpiSpanProps(w.config_json);
  const v = kpiValueForCode(w.widget_code, k);
  return (
    <Grid item xs={sp.xs} sm={sp.sm} md={sp.md} key={w.widget_code}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {w.widget_name}
        </Typography>
        <Typography variant="h6">{v != null && v !== '' ? v : '—'}</Typography>
      </Paper>
    </Grid>
  );
}

function LegacyKpiGrid({ k }) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={4} md={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Tenants
          </Typography>
          <Typography variant="h6">{k?.tenantCount ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Active tenants
          </Typography>
          <Typography variant="h6">{k?.activeTenants ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Tenant users
          </Typography>
          <Typography variant="h6">{k?.totalTenantUsers ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            API requests
          </Typography>
          <Typography variant="h6">{k?.tenantApiRequestsInRange ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Logins (range)
          </Typography>
          <Typography variant="h6">{k?.loginsInRange ?? '—'}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Distinct users w/ login
          </Typography>
          <Typography variant="h6">{k?.distinctTenantUsersWithLoginInRange ?? '—'}</Typography>
        </Paper>
      </Grid>
      {(k?.proctoringViolationsTotal != null || k?.proctoringSnapshotsTotal != null) && (
        <>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Violations (all time)
              </Typography>
              <Typography variant="h6">{k.proctoringViolationsTotal ?? '—'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Snapshots (all time)
              </Typography>
              <Typography variant="h6">{k.proctoringSnapshotsTotal ?? '—'}</Typography>
            </Paper>
          </Grid>
        </>
      )}
    </Grid>
  );
}

export default function PlatformAnalyticsPage() {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpiLayout, setKpiLayout] = useState(null);

  const q = useMemo(() => `from=${from}&to=${to}`, [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, tRes, topRes] = await Promise.all([
        platformApiFetch(`/api/platform/analytics/summary?${q}`),
        platformApiFetch(`/api/platform/analytics/timeseries?${q}`),
        platformApiFetch(`/api/platform/analytics/tenants/top?${q}&limit=15`)
      ]);
      const sJson = await sRes.json().catch(() => ({}));
      const tJson = await tRes.json().catch(() => ({}));
      const topJson = await topRes.json().catch(() => ({}));
      if (!sRes.ok) throw new Error(sJson.error || 'Summary failed');
      if (!tRes.ok) throw new Error(tJson.error || 'Timeseries failed');
      if (!topRes.ok) throw new Error(topJson.error || 'Top tenants failed');
      setSummary(sJson);
      setSeries(mergeSeries(tJson.usageByDay, tJson.loginsByDay));
      setTop(topJson);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const layRes = await platformApiFetch(
          `/api/platform/dashboard/layout?dashboard_type=${encodeURIComponent(PLATFORM_ANALYTICS_DASHBOARD_TYPE)}`
        );
        const layJson = await layRes.json().catch(() => ({}));
        if (cancelled) return;
        if (layRes.ok && layJson && !layJson.useLegacyDefaults && layJson.widgets?.length) {
          setKpiLayout(layJson);
        } else {
          setKpiLayout(null);
        }
      } catch (_e) {
        if (!cancelled) setKpiLayout(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const k = summary && summary.kpis;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Platform analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scope: <strong>all tenants</strong> (aggregated). Proctoring totals are global and not tenant-attributed
        until you add <code>tenant_id</code> on legacy tables.
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
        />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !summary ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {kpiLayout && kpiLayout.widgets && kpiLayout.widgets.length > 0 ? (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {kpiLayout.widgets.map((w) => renderConfiguredKpi(w, k))}
            </Grid>
          ) : (
            <LegacyKpiGrid k={k} />
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Platform activity by day
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="apiRequests" name="API requests" stroke="#1976d2" dot={false} />
                    <Line type="monotone" dataKey="loginsExact" name="Logins" stroke="#2e7d32" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Top tenants by API requests
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={
                      (top && top.tenants && top.tenants.length
                        ? top.tenants.slice(0, 8)
                        : [{ slug: '—', apiRequests: 0 }]
                      ).map((t) => ({
                        name: t.slug,
                        api: t.apiRequests
                      }))
                    }
                    margin={{ bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="api" name="API reqs" fill="#9c27b0" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Tenant leaderboard (range)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">API reqs</TableCell>
                      <TableCell align="right">Logins</TableCell>
                      <TableCell align="right">Distinct users</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((top && top.tenants) || []).map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>
                          {t.name} <Typography component="span" variant="caption" color="text.secondary">({t.slug})</Typography>
                        </TableCell>
                        <TableCell>{t.status}</TableCell>
                        <TableCell align="right">{t.apiRequests}</TableCell>
                        <TableCell align="right">{t.loginsExactInRange}</TableCell>
                        <TableCell align="right">{t.distinctUsersWithLoginInRange}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
